import { useState, useEffect, useCallback } from "react";
import CustomModal from "../../Common/CustomModal";
import FormField from "../../FormField/FormField";
import { updateOrganization, getMuseumDetails, fileUpload } from "../../../api";
import AuthenticatedFileImage from "../../Common/AuthenticatedFileImage/AuthenticatedFileImage";
import { toast } from "react-toastify";
import "./EditOrganizationModal.scss";

const EditOrganizationModal = ({
  show,
  organizationData,
  fetchOrganizations,
  handleClose,
}) => {
  const [isLocalOrganization, setIsLocalOrganization] = useState(
    organizationData?.localId === true
  );

  const [formData, setFormData] = useState({
    name: "",
    comune: "",
    address: "",
    thumbnailUrl: "",
    logoUrl: "",
  });
  const [logoPreview, setLogoPreview] = useState("");
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);

  useEffect(() => {
    if (!show || !organizationData) return;

    const initialName = organizationData.name || "";
    const initialComune =
      typeof organizationData.comune === "string"
        ? organizationData.comune
        : organizationData.comune?.nome || "";

    const initialData = {
      name: initialName,
      comune: initialComune,
      address: organizationData.address || organizationData.indirizzo || "",
      thumbnailUrl: organizationData.thumbnailUrl || "",
      logoUrl: organizationData.logoUrl || "",
    };

    setFormData(initialData);
    setLogoPreview("");
    setIsLocalOrganization(organizationData.localId === true);

    const isCatalogOrganization = organizationData.localId === false;
    if (!isCatalogOrganization) return;

    let cancelled = false;

    const fetchMuseumDetails = async () => {
      setIsLoadingDetails(true);
      try {
        const details = await getMuseumDetails(organizationData.id);
        if (cancelled) return;

        const catalogName = details.denominazione || "";
        const catalogComune = details.nome_comune || "";
        const hasCustomName =
          initialName.trim() !== "" && initialName !== catalogName;
        const hasCustomComune =
          initialComune.trim() !== "" && initialComune !== catalogComune;

        if (hasCustomName || hasCustomComune) {
          setIsLocalOrganization(true);
        }

        setFormData((prev) => ({
          name: prev.name || catalogName,
          comune: prev.comune || catalogComune,
          address: prev.address || details.indirizzo || "",
          thumbnailUrl: prev.thumbnailUrl || details.immagine_copertina || "",
          logoUrl: prev.logoUrl,
        }));
      } catch (error) {
        console.error("Error fetching museum details", error);
        if (!cancelled) {
          setIsLocalOrganization(true);
          toast.error("Errore nel recupero dei dati da Musei Italiani");
        }
      } finally {
        if (!cancelled) {
          setIsLoadingDetails(false);
        }
      }
    };

    fetchMuseumDetails();

    return () => {
      cancelled = true;
    };
  }, [show, organizationData]);

  const handleFieldChange = useCallback((key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleLogoUpload = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploadingLogo(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append("file", file);
      const response = await fileUpload(uploadFormData);
      const uploadedLogoUrl = response.id;
      setFormData((prev) => ({ ...prev, logoUrl: uploadedLogoUrl }));
      setLogoPreview(URL.createObjectURL(file));
      toast.success("Logo caricato con successo");
    } catch (error) {
      console.error("Error uploading logo", error);
      toast.error("Errore nel caricamento del logo");
    } finally {
      setIsUploadingLogo(false);
      event.target.value = "";
    }
  }, []);

  const handleSubmit = async () => {

    if (isLocalOrganization) {
      if (!formData.name?.trim()) {
        toast.error("Inserisci il nome dell'organizzazione");
        return;
      }
      if (!formData.comune?.trim()) {
        toast.error("Inserisci il comune");
        return;
      }
    }

    const submitData = {
      id: organizationData.id,
      name: formData.name.trim(),
      comune: formData.comune.trim(),
      thumbnailUrl: formData.thumbnailUrl || "",
      logoUrl: formData.logoUrl,
    };

    try {
      await updateOrganization(submitData);
      handleClose();
      toast.success("Organizzazione aggiornata con successo");
      fetchOrganizations();
    } catch (err) {
      console.error("Errore nell'aggiornamento dell'organizzazione", err);
      toast.error("Errore nell'aggiornamento dell'organizzazione");
    }
  };

  return (
    <CustomModal
      isVisible={show}
      onClose={handleClose}
      title="Modifica organizzazione"
    >
      <div className="edit-organization-modal-body">
        {isLoadingDetails && (
          <p className="edit-organization-modal-body__loading">
            Caricamento dati da Musei Italiani...
          </p>
        )}

        <FormField
          fieldStructure={{
            label: "Nome organizzazione",
            type: "text",
            editable: isLocalOrganization,
          }}
          value={formData.name}
          onChange={(value) => handleFieldChange("name", value)}
          disabled={!isLocalOrganization}
        />

        <FormField
          fieldStructure={{
            label: "Comune o sito",
            type: "text",
            editable: isLocalOrganization,
          }}
          value={formData.comune}
          onChange={(value) => handleFieldChange("comune", value)}
          disabled={!isLocalOrganization}
        />

        {!isLocalOrganization && (
          <div className="input-wrapper">
            <span className="input-label">Indirizzo</span>
            <p className="organization-readonly-field">
              {formData.address || "—"}
            </p>
          </div>
        )}

        {formData.thumbnailUrl && (
          <div className="input-wrapper organization-thumbnail-preview">
            <span className="input-label">Anteprima copertina</span>
            <img src={formData.thumbnailUrl} alt="Anteprima organizzazione" />
          </div>
        )}

        <div className="input-wrapper">
          <label htmlFor="edit-organization-logo" className="input-label">
            Logo organizzazione
          </label>
          <div className="organization-logo-upload">
            <label
              className="organization-logo-upload__button"
              htmlFor="edit-organization-logo"
            >
              {isUploadingLogo ? "Caricamento..." : "Carica logo"}
            </label>
            <input
              id="edit-organization-logo"
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              onChange={handleLogoUpload}
              disabled={isUploadingLogo}
              hidden
            />
            {logoPreview ? (
              <img
                className="organization-logo-upload__preview"
                src={logoPreview}
                alt="Anteprima logo"
              />
            ) : (
              formData.logoUrl && (
                <AuthenticatedFileImage
                  src={formData.logoUrl}
                  alt="Logo organizzazione"
                  className="organization-logo-upload__preview"
                />
              )
            )}
          </div>
        </div>
      </div>
      <footer className="modal-footer">
        <button
          type="submit"
          className="modal-footer-button"
          onClick={handleSubmit}
          disabled={isLoadingDetails || isUploadingLogo}
        >
          Salva
        </button>
      </footer>
    </CustomModal>
  );
};

export default EditOrganizationModal;
